import React, { useState, useEffect, useRef } from 'react';
import { BicycleSpace, BicycleType, BicycleAddon, BicycleAddonRef } from '../types/bicycle';
import { bicycleTypeService, bicycleAddonService, bicycleSpaceService } from '../services/bicycleService';
import { unitService } from '../services/unitService';
import { Unit } from '../types/unit';
import { Trash2, X } from 'lucide-react';
import '../styles/FloorLayoutEditor.css';

interface BicycleSpaceEditModalProps {
  isOpen: boolean;
  workspaceId: string;
  projectId: string;
  space: BicycleSpace | null;
  onClose: () => void;
  onSave: (updatedSpace: any) => Promise<void>;
  onDelete?: (spaceId: string) => Promise<void>;
}

const BicycleSpaceEditModal: React.FC<BicycleSpaceEditModalProps> = ({
  isOpen,
  workspaceId,
  projectId,
  space,
  onClose,
  onSave,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    type: '',
    status: 'AVAILABLE' as BicycleSpace['status'],
    isPremium: false,
    unitId: '',
    unitNo: ''
  });

  const [bicycleTypes, setBicycleTypes] = useState<BicycleType[]>([]);
  const [availableAddons, setAvailableAddons] = useState<BicycleAddon[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<BicycleAddonRef[]>([]);

  const [units, setUnits] = useState<Unit[]>([]);
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const unitInputRef = useRef<HTMLInputElement>(null);
  const unitDropdownRef = useRef<HTMLDivElement>(null);

  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && space) {
      setFormData({
        type: space.type,
        status: space.status,
        isPremium: space.isPremium,
        unitId: space.unitId || '',
        unitNo: space.unitNo || ''
      });
      setUnitSearchQuery(space.unitNo || '');
      setSelectedAddons(space.addons || []);
      fetchBicycleTypes();
      fetchAvailableAddons();
    }
  }, [isOpen, space]);

  // Search units when query changes (with debounce)
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      if (unitSearchQuery.length > 0) {
        fetchUnits(unitSearchQuery);
      } else {
        setUnits([]);
        setShowUnitDropdown(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayTimer);
  }, [unitSearchQuery]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        unitDropdownRef.current &&
        !unitDropdownRef.current.contains(event.target as Node) &&
        unitInputRef.current &&
        !unitInputRef.current.contains(event.target as Node)
      ) {
        setShowUnitDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchBicycleTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await bicycleTypeService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setBicycleTypes(response.bicycleTypes || []);
    } catch (error) {
      console.error('Error fetching bicycle types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchAvailableAddons = async () => {
    try {
      setLoadingAddons(true);
      const response = await bicycleAddonService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setAvailableAddons(response.bicycleAddons || []);
    } catch (error) {
      console.error('Error fetching bicycle addons:', error);
    } finally {
      setLoadingAddons(false);
    }
  };

  const fetchUnits = async (search: string) => {
    try {
      setLoadingUnits(true);
      const response = await unitService.getAll(workspaceId, {
        projectId,
        search,
        limit: 50
      });
      setUnits(response.units || []);
      setShowUnitDropdown(true);
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleSelectUnit = (unit: Unit) => {
    setFormData({
      ...formData,
      unitId: unit._id,
      unitNo: unit.unitNo
    });
    setUnitSearchQuery(unit.unitNo);
    setShowUnitDropdown(false);
  };

  const handleUnitInputChange = (value: string) => {
    setUnitSearchQuery(value);
    setFormData({
      ...formData,
      unitId: '',
      unitNo: value
    });
  };

  const handleAddAddon = (addon: BicycleAddon) => {
    // Check if already added
    if (selectedAddons.some(a => a._id === addon._id)) {
      return;
    }

    const addonRef: BicycleAddonRef = {
      _id: addon._id,
      name: addon.name,
      color: addon.color,
      cost: addon.cost,
      price: addon.price,
      mandatory: false
    };

    setSelectedAddons([...selectedAddons, addonRef]);
  };

  const handleRemoveAddon = (addonId: string) => {
    setSelectedAddons(selectedAddons.filter(a => a._id !== addonId));
  };

  const toggleAddonMandatory = (addonId: string) => {
    setSelectedAddons(selectedAddons.map(a =>
      a._id === addonId ? { ...a, mandatory: !a.mandatory } : a
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.type) {
      setError('Type is required');
      return;
    }

    try {
      setSaving(true);

      const updateData = {
        type: formData.type,
        status: formData.status,
        isPremium: formData.isPremium,
        unitId: formData.unitId || undefined,
        unitNo: formData.unitNo || undefined,
        addons: selectedAddons
      };

      await bicycleSpaceService.update(workspaceId, space!._id, updateData);
      await onSave(updateData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update space');
      console.error('Error updating space:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !space) return;

    try {
      setSaving(true);
      await onDelete(space._id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete space');
      console.error('Error deleting space:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !space) return null;

  return (
    <div className="floor-layout-modal-overlay" onClick={onClose}>
      <div
        className="floor-layout-modal"
        style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="floor-layout-modal-header">
          <h3>Edit Bicycle Space: {space.number}</h3>
          <button className="floor-layout-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="floor-layout-modal-body">
            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#FEE2E2',
                border: '1px solid #FCA5A5',
                borderRadius: '6px',
                color: '#991B1B',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {/* Space Number - Read Only */}
            <div className="model-details-field">
              <label>Space Number</label>
              <input
                type="text"
                className="form-input"
                value={space.number}
                disabled
                style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
              />
            </div>

            {/* Type Dropdown */}
            <div className="model-details-field">
              <label>Type *</label>
              {loadingTypes ? (
                <div style={{ padding: '10px', textAlign: 'center', color: '#6B7280' }}>
                  Loading types...
                </div>
              ) : (
                <select
                  className="form-input"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="">Select Type</option>
                  {bicycleTypes.map((type) => (
                    <option key={type._id} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Status Dropdown */}
            <div className="model-details-field">
              <label>Status *</label>
              <select
                className="form-input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as BicycleSpace['status'] })}
                required
              >
                <option value="AVAILABLE">Available</option>
                <option value="MARKETING HOLD">Marketing Hold</option>
                <option value="SALES HOLD">Sales Hold</option>
                <option value="CUSTOMER SELECTION">Customer Selection</option>
                <option value="CUSTOMER SUBMITTED">Customer Submitted</option>
                <option value="SOLD">Sold</option>
              </select>
            </div>

            {/* Premium Checkbox */}
            <div className="model-details-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isPremium}
                  onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                />
                Premium Space
              </label>
            </div>

            {/* Unit Assignment with Autocomplete */}
            <div className="model-details-field" style={{ position: 'relative' }}>
              <label>Assigned Unit</label>
              <input
                ref={unitInputRef}
                type="text"
                className="form-input"
                value={unitSearchQuery}
                onChange={(e) => handleUnitInputChange(e.target.value)}
                onFocus={() => {
                  if (unitSearchQuery && units.length > 0) {
                    setShowUnitDropdown(true);
                  }
                }}
                placeholder="Type to search units..."
                autoComplete="off"
              />
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Enter unit number to assign this space
              </p>

              {/* Autocomplete Dropdown */}
              {showUnitDropdown && (
                <div
                  ref={unitDropdownRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '-20px',
                    backgroundColor: 'white',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000
                  }}
                >
                  {loadingUnits ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
                      Loading units...
                    </div>
                  ) : units.length > 0 ? (
                    units.map((unit) => (
                      <div
                        key={unit._id}
                        onClick={() => handleSelectUnit(unit)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #F3F4F6',
                          fontSize: '13px',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{ fontWeight: '500', color: '#111827' }}>
                          {unit.unitNo}
                        </div>
                        {unit.modelName && (
                          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                            {unit.modelName} {unit.bed && `• ${unit.bed} Bed`} {unit.bath && `• ${unit.bath} Bath`}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
                      No units found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Addons Section */}
            <div className="model-details-field">
              <label>Addons</label>

              {/* Selected Addons List */}
              {selectedAddons.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  {selectedAddons.map((addon) => (
                    <div
                      key={addon._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        marginBottom: '8px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#111827' }}>
                          {addon.name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                          Cost: ${addon.cost.toLocaleString()} | Price: ${addon.price.toLocaleString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={addon.mandatory || false}
                            onChange={() => toggleAddonMandatory(addon._id)}
                          />
                          Mandatory
                        </label>
                        <button
                          type="button"
                          onClick={() => handleRemoveAddon(addon._id)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#FEE2E2',
                            color: '#991B1B',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Available Addons Dropdown */}
              {loadingAddons ? (
                <div style={{ padding: '10px', textAlign: 'center', color: '#6B7280' }}>
                  Loading addons...
                </div>
              ) : availableAddons.length > 0 ? (
                <select
                  className="form-input"
                  value=""
                  onChange={(e) => {
                    const addon = availableAddons.find(a => a._id === e.target.value);
                    if (addon) {
                      handleAddAddon(addon);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">+ Add Addon</option>
                  {availableAddons
                    .filter(addon => !selectedAddons.some(a => a._id === addon._id))
                    .map((addon) => (
                      <option key={addon._id} value={addon._id}>
                        {addon.name} (${addon.price})
                      </option>
                    ))}
                </select>
              ) : (
                <p style={{ fontSize: '13px', color: '#6B7280', fontStyle: 'italic' }}>
                  No addons available for this project
                </p>
              )}
            </div>

            {/* Position Info (Read-only) */}
            {space.spaceObject && (
              <div className="model-details-field">
                <label>Position on Layout</label>
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#F3F4F6',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#374151'
                }}>
                  X: {Math.round(space.spaceObject.left || 0)},
                  Y: {Math.round(space.spaceObject.top || 0)},
                  Angle: {Math.round(space.spaceObject.angle || 0)}°
                </div>
              </div>
            )}
          </div>

          <div className="floor-layout-modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              {onDelete && !showDeleteConfirm && (
                <button
                  type="button"
                  className="btn btn-danger btn-md"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Trash2 size={16} />
                  Delete Space
                </button>
              )}
              {showDeleteConfirm && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: '#991B1B', fontSize: '14px', fontWeight: '500' }}>
                    Confirm delete?
                  </span>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    Yes, Delete
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-md"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BicycleSpaceEditModal;
